import { AnalyticsAdapter, AnalyticsConfig, UserTraits, EventProperties, PageData } from './types';

class ConsentChecker {
  private static STORAGE_KEY = 'voopty_consent_preferences';

  static canTrackAnalytics(): boolean {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return false;

      const consent = JSON.parse(stored);
      return consent?.analytics === true;
    } catch (error) {
      return false;
    }
  }

  static canTrackMarketing(): boolean {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return false;

      const consent = JSON.parse(stored);
      return consent?.marketing === true;
    } catch (error) {
      return false;
    }
  }

  static getConsent(): any {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  }
}

export class AnalyticsService {
  private adapters: Map<string, AnalyticsAdapter> = new Map();
  protected config: AnalyticsConfig;
  private eventQueue: Array<{ method: string; args: any[] }> = [];
  protected initialized = false;

  constructor(config: AnalyticsConfig) {
    this.config = config;
  }

  async initialize(adapters: AnalyticsAdapter[]): Promise<void> {
    if (!this.config.enabled) {
      // console.log('📊 Analytics is disabled');
      return;
    }

    // console.log('🚀 Starting analytics initialization...');

    try {
      // Ініціалізуємо всі адаптери
      const initPromises = adapters.map(async (adapter) => {
        // console.log(`🔧 Initializing ${adapter.name} adapter...`);
        await adapter.initialize(this.config);
        this.adapters.set(adapter.name, adapter);
        // console.log(`✅ ${adapter.name} adapter initialized`);
      });

      await Promise.all(initPromises);

      this.initialized = true;

      // Flush queue after initialization
      this.flushQueue();

      // Make available in window for debugging
      window.analyticsService = this;

      // console.log('✅ Analytics initialization completed successfully');

      // if (this.config.debug) {
      //   setTimeout(() => {
      //     this.track('Analytics Initialized', {
      //       adapters_count: this.adapters.size,
      //       queue_size_flushed: this.eventQueue.length,
      //       test: true
      //     });
      //   }, 1000);
      // }
    } catch (error) {
      console.error('❌ Analytics initialization error:', error);
    }
  }

  identify(userId: string, traits?: UserTraits): void {
    if (!ConsentChecker.canTrackAnalytics()) {
      // console.log('🚫 User identification blocked - no analytics consent');
      return;
    }

    if (!this.initialized) {
      this.eventQueue.push({ method: 'identify', args: [userId, traits] });
      // console.log('📋 Identify queued:', userId);
      return;
    }

    let successCount = 0;
    this.adapters.forEach((adapter) => {
      try {
        adapter.identify(userId, traits);
        successCount++;
      } catch (error) {
        // console.error(`❌ Identify error in ${adapter.name}:`, error);
      }
    });

    if (this.config.debug) {
      // console.log(`👤 User identified in ${successCount}/${this.adapters.size} adapters:`, userId);
    }
  }

  track(eventName: string, properties?: EventProperties): void {
    if (!ConsentChecker.canTrackAnalytics()) {
      // console.log('🚫 Event tracking blocked - no analytics consent:', eventName);
      return;
    }

    if (!this.initialized) {
      this.eventQueue.push({ method: 'track', args: [eventName, properties] });
      // console.log('📋 Event queued:', eventName, `(Queue size: ${this.eventQueue.length})`);
      return;
    }

    const enhancedProperties = {
      ...properties,
      marketing_consent: ConsentChecker.canTrackMarketing(),
      consent_level: this.getConsentLevel(),
    };

    let successCount = 0;
    this.adapters.forEach((adapter) => {
      try {
        adapter.track(eventName, enhancedProperties);
        successCount++;
      } catch (error) {
        console.error(`❌ Track error in ${adapter.name}:`, error);
      }
    });

    if (this.config.debug) {
      // console.log(`📊 Event "${eventName}" sent to ${successCount}/${this.adapters.size} adapters`);
    }
  }

  page(data?: PageData): void {
    if (!ConsentChecker.canTrackAnalytics()) {
      // console.log('🚫 Page tracking blocked - no analytics consent');
      return;
    }

    if (!this.initialized) {
      this.eventQueue.push({ method: 'page', args: [data] });
      // console.log('📋 Page view queued:', data?.path || window.location.pathname);
      return;
    }

    let successCount = 0;
    this.adapters.forEach((adapter) => {
      try {
        adapter.page(data);
        successCount++;
      } catch (error) {
        console.error(`❌ Page error in ${adapter.name}:`, error);
      }
    });

    if (this.config.debug) {
      // console.log(`📄 Page view sent to ${successCount}/${this.adapters.size} adapters:`, data?.path);
    }
  }

  reset(): void {
    this.adapters.forEach((adapter) => {
      try {
        adapter.reset();
      } catch (error) {
        console.error(`❌ Reset error in ${adapter.name}:`, error);
      }
    });
  }

  isFeatureEnabled(flag: string): boolean {
    for (const adapter of this.adapters.values()) {
      if (adapter.isFeatureEnabled) {
        return adapter.isFeatureEnabled(flag);
      }
    }
    return false;
  }

  getFeatureFlag(flag: string): string | boolean | undefined {
    for (const adapter of this.adapters.values()) {
      if (adapter.getFeatureFlag) {
        return adapter.getFeatureFlag(flag);
      }
    }
    return undefined;
  }

  private flushQueue(): void {
    if (this.eventQueue.length === 0) {
      // console.log('📤 Event queue is empty');
      return;
    }

    // console.log(`📤 Flushing event queue (${this.eventQueue.length} events)...`);

    const originalQueueSize = this.eventQueue.length;
    while (this.eventQueue.length > 0) {
      const { method, args } = this.eventQueue.shift()!;
      try {
        (this as any)[method](...args);
      } catch (error) {
        console.error(`❌ Error flushing ${method}:`, error);
      }
    }

    // console.log(`✅ Flushed ${originalQueueSize} events from queue`);
  }

  getStatus() {
    const consent = ConsentChecker.getConsent();
    return {
      initialized: this.initialized,
      adapters: Array.from(this.adapters.keys()),
      queueSize: this.eventQueue.length,
      config: this.config,
      consentGranted: ConsentChecker.canTrackAnalytics(),
      marketingConsent: ConsentChecker.canTrackMarketing(),
      consentDetails: consent,
    };
  }

  getAdapterStatus() {
    const status: any = {};
    this.adapters.forEach((adapter, name) => {
      status[name] = {
        loaded: adapter.loaded(),
        name: adapter.name,
      };
    });
    return status;
  }

  private getConsentLevel(): string {
    const consent = ConsentChecker.getConsent();
    if (!consent) return 'none';

    if (consent.analytics && consent.marketing) return 'full';
    if (consent.analytics) return 'analytics_only';
    if (consent.functional) return 'functional_only';

    return 'minimal';
  }

  clearAnalyticsData(): void {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('ph_') || key.startsWith('analytics_')) {
          localStorage.removeItem(key);
        }
      });

      this.reset();

      // console.log('🧹 Analytics data cleared');
    } catch (error) {
      console.error('Error clearing analytics data:', error);
    }
  }
}
